import apiClient from "./apiClient";
import { IDENTITY_SERVICE_BASE_URL } from "../constants";

class TokenRefreshService {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.onSessionExpired = null;
    this.onTokenRefreshed = null;
    this.idleTimeoutMs = 60 * 60 * 1000;
    this.refreshBufferMs = 2 * 60 * 1000;
    this.lastActivityTime = Date.now();
  }

  initialize(onSessionExpired, onTokenRefreshed) {
    this.cleanup();
    this.onSessionExpired = onSessionExpired;
    this.onTokenRefreshed = onTokenRefreshed;
    this.trackActivity();
    this.startRefreshTimer();
  }

  trackActivity() {
    const update = () => {
      this.lastActivityTime = Date.now();
    };

    ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"]
      .forEach(e => document.addEventListener(e, update, true));

    this.cleanupActivity = () => {
      ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"]
        .forEach(e => document.removeEventListener(e, update, true));
    };
  }

  isUserActive() {
    return Date.now() - this.lastActivityTime < this.idleTimeoutMs;
  }

  startRefreshTimer() {
    const raw = sessionStorage.getItem("accessTokenExpiresAt");
    if (!raw) return;

    const expiresAt = new Date(raw).getTime();
    const delay = expiresAt - Date.now() - this.refreshBufferMs;

    if (delay <= 0) {
      this.refreshTokens().catch(() => {
        this.handleSessionExpired();
      });
      return;
    }

    this.clearRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      if (this.isUserActive()) {
        this.refreshTokens();
      } else {
        this.handleSessionExpired();
      }
    }, delay);
  }

  async refreshTokens() {
    if (this.isRefreshing && this.refreshPromise) return this.refreshPromise;

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      const refreshToken = sessionStorage.getItem("refreshToken");
      const clientId = localStorage.getItem("clientId");

      if (!refreshToken || !clientId) {
        this.handleSessionExpired();
        return;
      }

      try {
        const { data } = await apiClient.post(
          `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/token/refresh`,
          { refreshToken, deviceId: clientId }
        );

        if (!data?.accessToken) {
          this.handleSessionExpired();
          return;
        }

        sessionStorage.setItem("authToken", data.accessToken);
        sessionStorage.setItem("refreshToken", data.refreshToken);
        sessionStorage.setItem("accessTokenExpiresAt", data.accessTokenExpiresAt);
        sessionStorage.setItem("refreshTokenExpiresAt", data.refreshTokenExpiresAt);

        apiClient.defaults.headers.Authorization = `Bearer ${data.accessToken}`;

        this.startRefreshTimer();

        this.onTokenRefreshed?.(data);
      } catch (error) {
        this.handleSessionExpired();
        throw error;
      }
    })().finally(() => {
      this.isRefreshing = false;
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  handleSessionExpired() {
    this.cleanup();
    this.onSessionExpired?.();
  }

  cleanup() {
    this.clearRefreshTimer();
    this.cleanupActivity?.();
  }
}

export default new TokenRefreshService();
